import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getProgram, listProjectsForProgram } from "../../queries/workspace";

/**
 * Swap a pre-PLEX-6051 workspace URL for the current ids.
 *
 * Program and project ids used to be name slugs (/v2/p/kras/screen). The
 * migration gave every program and project a server-minted uuid and kept the
 * slug on the row as `legacyId`, so bookmarks and old links still carry slugs.
 * When the URL holds an id that isn't a uuid:
 *
 *   - the program GET answers with the program the slug became (the backend
 *     follows the old row's `migratedTo`);
 *   - the project is looked up by `legacyId` among that program's projects. Not
 *     by its own GET: a project slug alone can name another program's project,
 *     which is the collision the migration fixed.
 *
 * Then the URL is replaced (no history entry) with the current ids, keeping any
 * /c/<chatId> tail, query and hash. Anything that doesn't resolve is left as it
 * is, so the page shows its usual not-found or permission state.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export const isLegacyId = (id) => typeof id === "string" && id !== "" && !UUID_RE.test(id);

export const resolveCurrentIds = async (authData, program, project) => {
  let programId = program;
  if (isLegacyId(program)) {
    const found = await getProgram(authData, program);
    programId = found?.id || program;
  }
  let projectId = project;
  if (project && isLegacyId(project)) {
    // A failed lookup keeps the program already resolved: the project stays as it is, as for
    // a slug with no match (PR #146 auto-review).
    const projects = await listProjectsForProgram(authData, programId).catch(() => null);
    const match = (projects || []).find((p) => p.legacyId === project);
    projectId = match?.id || project;
  }
  return { programId, projectId };
};

export const currentPath = (pathname, { program, project }, { programId, projectId }) => {
  const prefix = `/v2/p/${program}${project ? `/${project}` : ""}`;
  if (!pathname.startsWith(prefix)) return null;
  const rest = pathname.slice(prefix.length);
  if (rest && !rest.startsWith("/")) return null;
  return `/v2/p/${programId}${project ? `/${projectId}` : ""}${rest}`;
};

export const useCurrentWorkspaceIds = (authData, { program, project } = {}) => {
  const navigate = useNavigate();
  // Read at resolve time, so a chat switch while the lookup is in flight keeps its tail.
  const locationRef = useRef(null);
  locationRef.current = useLocation();
  useEffect(() => {
    if (!authData || !(isLegacyId(program) || isLegacyId(project))) return undefined;
    let cancelled = false;
    resolveCurrentIds(authData, program, project)
      .then((ids) => {
        if (cancelled || (ids.programId === program && ids.projectId === project)) return;
        const { pathname, search, hash } = locationRef.current;
        const path = currentPath(pathname, { program, project }, ids);
        if (path) navigate(`${path}${search}${hash}`, { replace: true });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [authData, program, project, navigate]);
};
